import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Brief, Live } from "@/lib/intake";

/** The intake's cards, as in mobile/src/newtopic/topic_flow.tsx: one at a time, over the galaxy. */
export const GOLD = "#FFD36E";

export function Kicker({ children }: { children: ReactNode }) {
  return <p className="text-xs font-black tracking-[0.1em] text-[#FFD36E] uppercase">{children}</p>;
}

/** Jake's lesson card, with the charting progress as one line on top. */
export function Sheet({ children, status, label }: { children: ReactNode; status?: string; label: string }) {
  return (
    <div className="la-pop-in" role="group" aria-label={label}>
      {status && (
        <div className="flex items-center justify-center gap-2 pb-3" aria-live="polite">
          <span className="size-[7px] shrink-0 rounded-full bg-[#FFD36E] la-glow" />
          <p className="truncate text-[13px] font-extrabold text-[rgba(220,215,255,0.75)]">{status}</p>
        </div>
      )}
      <div className="flex flex-col gap-3 rounded-[24px] border border-white/15 bg-[rgba(24,16,60,0.94)] px-4 pt-[18px] pb-4 shadow-[0_24px_80px_-20px_rgba(10,6,40,0.9)] backdrop-blur-md sm:px-5">
        {children}
      </div>
    </div>
  );
}

export function GoldButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="h-[52px] w-full rounded-2xl border-b-[5px] border-[#C9962B] bg-[#FFD36E] text-[17px] font-black tracking-[0.02em] text-[#2A1A00] transition active:translate-y-[3px] active:border-b-2 disabled:opacity-60"
    >
      {children}
    </button>
  );
}

export function QuietButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="self-center px-3 py-1 text-sm font-extrabold text-[rgba(220,215,255,0.75)] hover:text-white">
      {children}
    </button>
  );
}

/** Free text, never truncated: grows to about five lines, then scrolls. Enter sends, shift+Enter is a new line. */
export function FreeText({ value, onChange, onSubmit, placeholder, label }: { value: string; onChange: (t: string) => void; onSubmit: () => void; placeholder: string; label: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(132, el.scrollHeight)}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      aria-label={label}
      rows={1}
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          onSubmit();
        }
      }}
      placeholder={placeholder}
      className="min-h-[50px] w-full resize-none rounded-2xl border-2 border-[#FFD36E] bg-white/[0.07] px-4 py-[12px] text-base leading-snug font-extrabold text-white placeholder:text-white/45 focus:outline-none"
    />
  );
}

function Chip({ label, on, wide, onClick }: { label: string; on: boolean; wide: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`min-h-12 rounded-[14px] border-2 px-3 py-2 text-[15px] leading-tight font-extrabold text-white transition ${wide ? "w-full text-left" : "grow basis-[40%] text-center"} ${
        on ? "border-[#8BE07A] bg-[rgba(139,224,122,0.18)]" : "border-white/15 bg-white/[0.07] hover:border-white/35 hover:bg-white/[0.1]"
      }`}
    >
      {label}
    </button>
  );
}

/** The adaptive intake's question: smart-guess chips, "Something else…" for their own words, Skip. */
export function QuestionCard({
  prompt,
  chips,
  n,
  status,
  onChip,
  onText,
  onSkip,
}: {
  prompt: string;
  chips: string[];
  n: number;
  status?: string;
  onChip: (label: string) => void;
  onText: (text: string) => void;
  onSkip: () => void;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [typing, setTyping] = useState(chips.length === 0);
  const [text, setText] = useState("");
  const choose = (label: string) => {
    if (chosen) return;
    setChosen(label);
    setTimeout(() => onChip(label), 380);
  };
  const submit = () => text.trim() && onText(text.trim());
  const short = chips.every(c => c.length <= 24);
  return (
    <Sheet status={status} label="Question">
      <div className="flex items-center">
        <div className="flex-1">
          <Kicker>{n === 1 ? "Quick question" : `Question ${n}`}</Kicker>
        </div>
        <button type="button" onClick={onSkip} className="-my-2 -mr-2 px-2 py-2 text-[13px] font-black tracking-[0.06em] text-white/60 uppercase hover:text-white">
          Skip
        </button>
      </div>
      <h2 className="text-[19px] leading-[1.22] font-black text-white sm:text-xl">{prompt}</h2>
      {typing ? (
        <>
          <FreeText value={text} onChange={setText} onSubmit={submit} placeholder="In your own words…" label="Your answer" />
          <GoldButton onClick={submit} disabled={!text.trim()}>
            Send
          </GoldButton>
          {chips.length > 0 && <QuietButton onClick={() => setTyping(false)}>Back to suggestions</QuietButton>}
        </>
      ) : (
        <>
          <div className={short ? "flex flex-wrap gap-2" : "flex flex-col gap-2"}>
            {chips.map(c => (
              <Chip key={c} label={c} on={chosen === c} wide={!short} onClick={() => choose(c)} />
            ))}
          </div>
          <QuietButton onClick={() => setTyping(true)}>Something else…</QuietButton>
        </>
      )}
    </Sheet>
  );
}

/** What the intake heard, before anything is written: one line in their terms, the details, confirm or edit. */
export function BriefCard({ brief, status, busy, onConfirm, onEdit }: { brief: Brief; status?: string; busy: boolean; onConfirm: () => void; onEdit: (text: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const rows = (
    [
      ["Starting from", brief.startingPoint],
      ["Fitting around", brief.constraints],
    ] as const
  ).filter(([, f]) => f?.value?.trim());
  const submit = () => text.trim() && onEdit(text.trim());
  return (
    <Sheet status={status} label="Here's what I heard">
      <Kicker>Here’s what I heard</Kicker>
      <div className="flex max-h-[min(280px,38svh)] flex-col gap-2.5 overflow-y-auto">
        <p className="text-lg leading-[1.3] font-black text-white">{brief.summary}</p>
        {rows.map(([k, f]) => (
          <div key={k} className="flex gap-2.5">
            <p className="w-[104px] shrink-0 text-[13px] font-black tracking-[0.04em] text-white/55">{k}</p>
            <p className={`flex-1 text-sm leading-snug font-extrabold ${f.confidence === "low" ? "text-white/60" : "text-white"}`}>
              {f.value}
              {f.confidence === "low" ? " (my guess)" : ""}
            </p>
          </div>
        ))}
      </div>
      {editing ? (
        <>
          <FreeText value={text} onChange={setText} onSubmit={submit} placeholder="What should I change?" label="What should I change?" />
          <GoldButton onClick={submit} disabled={!text.trim() || busy}>
            Update
          </GoldButton>
          <QuietButton onClick={() => setEditing(false)}>Cancel</QuietButton>
        </>
      ) : (
        <>
          <GoldButton onClick={onConfirm} disabled={busy}>
            That’s it, start
          </GoldButton>
          <QuietButton onClick={() => setEditing(true)}>Edit</QuietButton>
        </>
      )}
    </Sheet>
  );
}

/** Confirmed: the first lesson is being written. Hand off to the web app. */
export function ReadyCard({ live, status, onGo, onRestart }: { live: Live; status?: string; onGo: () => void; onRestart: () => void }) {
  const r = live.research;
  const ready = live.reel.beats.length > 0;
  const grounded = live.intake.trust === "grounded";
  return (
    <Sheet status={status} label="Your first lesson">
      <Kicker>{ready ? "Your first lesson is ready" : "Writing your first lesson"}</Kicker>
      <p className="text-[19px] leading-[1.25] font-black text-white sm:text-xl">{live.intake.title ?? live.intent?.brief?.summary ?? "Your course"}</p>
      {r.status !== "off" && (
        <div className="rounded-2xl bg-white/[0.06] px-3.5 py-3">
          <p className="text-[13px] font-black tracking-[0.04em] text-white/55">{grounded ? "Grounded in" : "Research"}</p>
          {r.sources.length ? (
            <ul className="mt-1.5 flex flex-col gap-1">
              {r.sources.slice(0, 4).map(s => (
                <li key={s} className="flex items-center gap-2 text-sm font-extrabold text-white">
                  <span className="size-1.5 shrink-0 rounded-full bg-[#FFD36E]" />
                  <span className="truncate">{s}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-sm leading-snug font-bold text-white/75">
              {r.status === "running" ? (r.log ?? "Finding sources in the background. Your first lesson doesn’t wait for them.") : "No sources confirmed yet, so lessons stick to well-established knowledge."}
            </p>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onGo}
        className="mt-1 h-[60px] w-full rounded-[18px] border-b-[5px] border-[#4128B8] bg-[#6B4EFF] text-lg font-black text-white shadow-[0_0_44px_rgba(107,78,255,0.55)] transition hover:brightness-110 active:translate-y-[3px] active:border-b-2"
      >
        Start my first lesson
      </button>
      <p className="text-center text-[13px] leading-snug font-bold text-white/55">Opens in the LearnAnything web app. Your answers come with you.</p>
      <QuietButton onClick={onRestart}>Start over</QuietButton>
    </Sheet>
  );
}
